import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import "./InstallPrompt.css";

// Detect iOS device (including modern iPads with desktop-like user agents)
const isIOS = () => {
  const ua = navigator.userAgent || '';
  // Check for explicit iOS/iPad in user agent
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // Modern iPads (iPadOS 13+) report as Mac but have touch capability
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  // Additional check: if device has touch AND is NOT Windows/Android, treat as iOS
  if ('ontouchstart' in window && navigator.maxTouchPoints > 1) {
    // Not Android and not Windows = likely iOS/iPadOS
    if (!/Android/i.test(ua) && !/Windows/i.test(ua)) return true;
  }
  return false;
};

// Check if device is a touch device (iPad, iPhone, Android tablet/phone)
const isTouchDevice = () => {
  return ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) ||
    (window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
};

// Detect Chrome on iOS
const isChromeiOS = () => {
  if (!isIOS()) return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('crios') || ua.includes('chrome');
};

// Detect Safari on iOS
const isSafariiOS = () => {
  if (!isIOS()) return false;
  const ua = navigator.userAgent.toLowerCase();
  // Safari on iOS doesn't have 'chrome' in user agent, but has 'safari'
  // Chrome on iOS has 'crios' or 'chrome' in user agent
  return ua.includes('safari') && !ua.includes('crios') && !ua.includes('chrome');
};

const InstallPrompt = ({ onInstall, isIOSDevice = false, isVisible: controlledVisible = null, showIOSInstructionsDirectly = false, onClose }) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(showIOSInstructionsDirectly);

  // Use controlled visibility if provided, otherwise use internal state
  const isVisible = controlledVisible !== null ? controlledVisible : internalVisible;

  useEffect(() => {
    // Only check localStorage if not controlled externally
    if (controlledVisible === null && !localStorage.getItem("dontShowInstallPrompt")) {
      setInternalVisible(true);
    }
  }, [controlledVisible]);

  useEffect(() => {
    // Update showIOSInstructions when prop changes
    if (showIOSInstructionsDirectly) {
      setShowIOSInstructions(true);
    }
  }, [showIOSInstructionsDirectly]);

  const handleInstall = () => {
    console.log("InstallPrompt handleInstall called", { isIOSDevice, isIOS: isIOS(), onInstall: !!onInstall });
    
    if (isIOSDevice || isIOS()) {
      // For iOS, show instructions instead of triggering install
      setShowIOSInstructions(true);
    } else {
      // For Android/Chrome, try to trigger the install prompt
      if (onInstall) {
        try {
          onInstall();
          // Close modal after triggering prompt (user will see browser's native prompt)
          if (controlledVisible === null) {
            setInternalVisible(false);
          } else {
            onClose?.();
          }
        } catch (error) {
          console.error("Error triggering install prompt:", error);
          // If native prompt fails, show instructions as fallback
          setShowIOSInstructions(true);
        }
      } else {
        console.warn("onInstall callback not provided, showing instructions");
        // If no callback, show instructions
        setShowIOSInstructions(true);
      }
    }
  };

  const handleDismiss = () => {
    if (controlledVisible === null) {
      setInternalVisible(false);
    } else {
      // If controlled, call onClose callback
      onClose?.();
    }
  };

  const handleDoNotShowAgain = () => {
    localStorage.setItem("dontShowInstallPrompt", "true");
    if (controlledVisible === null) {
      setInternalVisible(false);
    } else {
      // If controlled, call onClose callback
      onClose?.();
    }
  };

  if (!isVisible) return null;

  // iOS Instructions View
  if (showIOSInstructions) {
    const isChrome = isChromeiOS();
    const isSafari = isSafariiOS();

    return (
      <div className="install-prompt">
        <div className="install-prompt__content">
          <div className="install-prompt__icon">
            <Icon icon="material-symbols:install-mobile" width="28" height="28" />
          </div>
          <div className="install-prompt__text">
            <h3 className="install-prompt__title">Add to Home Screen</h3>
            <p className="install-prompt__desc">
              Follow these steps to install the app on your device
            </p>
          </div>
          <button
            className="install-prompt__close"
            onClick={handleDismiss}
            aria-label="Close"
          >
            <Icon icon="iconamoon:close" width="20" height="20" />
          </button>
        </div>

        {isChrome ? (
          /* Chrome on iOS (iOS 17+) - supports Add to Home Screen directly */
          <div className="install-prompt__ios-steps">
            <div className="install-prompt__step">
              <div className="install-prompt__step-number">1</div>
              <div className="install-prompt__step-content">
                <p className="install-prompt__step-text">
                  Tap the <strong>Share</strong> button <Icon icon="material-symbols:ios-share" width="16" height="16" style={{ display: 'inline', verticalAlign: 'middle' }} /> next to the address bar
                </p>
              </div>
            </div>
            <div className="install-prompt__step">
              <div className="install-prompt__step-number">2</div>
              <div className="install-prompt__step-content">
                <p className="install-prompt__step-text">
                  Tap <strong>"Add to Home Screen"</strong> <Icon icon="mdi:plus-box-outline" width="16" height="16" style={{ display: 'inline', verticalAlign: 'middle' }} />
                </p>
              </div>
            </div>
            <div className="install-prompt__step">
              <div className="install-prompt__step-number">3</div>
              <div className="install-prompt__step-content">
                <p className="install-prompt__step-text">
                  Tap <strong>"Add"</strong> in the top right corner to confirm
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Safari on iOS */
          <div className="install-prompt__ios-steps">
            <div className="install-prompt__step">
              <div className="install-prompt__step-number">1</div>
              <div className="install-prompt__step-content">
                <p className="install-prompt__step-text">
                  Tap the <strong>Share</strong> button <Icon icon="material-symbols:ios-share" width="16" height="16" style={{ display: 'inline', verticalAlign: 'middle' }} /> at the bottom of Safari
                </p>
              </div>
            </div>
            <div className="install-prompt__step">
              <div className="install-prompt__step-number">2</div>
              <div className="install-prompt__step-content">
                <p className="install-prompt__step-text">
                  Scroll down and tap <strong>"Add to Home Screen"</strong> <Icon icon="mdi:plus-box-outline" width="16" height="16" style={{ display: 'inline', verticalAlign: 'middle' }} />
                </p>
              </div>
            </div>
            <div className="install-prompt__step">
              <div className="install-prompt__step-number">3</div>
              <div className="install-prompt__step-content">
                <p className="install-prompt__step-text">
                  Tap <strong>"Add"</strong> in the top right corner to confirm
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="install-prompt__actions">
          <button
            className="install-prompt__btn install-prompt__btn--primary"
            onClick={handleDismiss}
          >
            Got it
          </button>
          <button
            className="install-prompt__btn install-prompt__btn--secondary"
            onClick={handleDoNotShowAgain}
          >
            Don't show again
          </button>
        </div>
      </div>
    );
  }

  // Default Install Prompt View
  // For desktop browsers, show instructions if native prompt isn't available
  // IMPORTANT: Check if iOS/touch device first - iPads in landscape mode have width >= 992 but should NOT show desktop instructions
  const isIOSDeviceDetected = isIOS() || isIOSDevice;
  const isTouchDeviceDetected = isTouchDevice();
  // Only show desktop instructions if NOT iOS, NOT touch device, and screen is wide enough
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 992 && !isIOSDeviceDetected && !isTouchDeviceDetected;

  return (
    <div className="install-prompt">
      <div className="install-prompt__content">
        <div className="install-prompt__icon">
          <Icon icon="material-symbols:install-mobile" width="28" height="28" />
        </div>
        <div className="install-prompt__text">
          <h3 className="install-prompt__title">Install Sponsor App</h3>
          <p className="install-prompt__desc">
            {isIOSDeviceDetected
              ? "Add to your home screen for quick access"
              : isDesktop
                ? "Click Install to add this app to your browser, or use your browser's install option"
                : "Add to your home screen for quick access"
            }
          </p>
        </div>
        <button
          className="install-prompt__close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          <Icon icon="iconamoon:close" width="20" height="20" />
        </button>
      </div>
      {isDesktop && !isIOSDeviceDetected && (
        <div className="install-prompt__desktop-instructions">
          <p className="install-prompt__desktop-text">
            <strong>Desktop Installation:</strong>
          </p>
          <p className="install-prompt__desktop-text">
            Look for the install icon in your browser's address bar, or use the browser menu to install this app.
          </p>
          <p className="install-prompt__desktop-text">
            In Chrome/Edge: Click the install icon <Icon icon="material-symbols:install-desktop" width="16" height="16" style={{ display: 'inline', verticalAlign: 'middle' }} /> in the address bar
          </p>
        </div>
      )}
      <div className="install-prompt__actions">
        <button
          className="install-prompt__btn install-prompt__btn--primary"
          onClick={handleInstall}
        >
          {isDesktop && !isIOSDeviceDetected ? "Try Install" : "Install"}
        </button>
        <button
          className="install-prompt__btn install-prompt__btn--secondary"
          onClick={handleDoNotShowAgain}
        >
          Don't show again
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
